// src/pages/HomePage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonIcon,
  IonSkeletonText,
  IonFab,
  IonFabButton,
  IonFabList,
} from '@ionic/react';
import {
  checkmarkCircle,
  calendarOutline,
  peopleOutline,
  addOutline,
  chevronForwardOutline,
  timeOutline,
  megaphoneOutline,
  cartOutline,
  listOutline,
  closeOutline,
} from 'ionicons/icons';
import { supabase } from '../services/supabase';
import type { ChoreAssignment, CalendarEvent, FamilyMember } from '../types';

// UI Components
import { PageShell } from '../components/ui/PageShell';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { List, type ListItemProps } from '../components/ui/List';

// Layout Primitives
import { Container, Stack, AutoGrid, Row, Col } from '../components/ui/Layout';

// Auth
import { useAuth, useHouseholdId } from '../contexts/AuthContext';

import './HomePage.css';

// Get greeting based on time of day
const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

// Format date for display
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

// Format time for events
const formatEventTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const HomePage: React.FC = () => {
  const history = useHistory();
  const { userProfile, loading: authLoading } = useAuth();
  const householdId = useHouseholdId();

  const [loading, setLoading] = useState(true);
  const [todayChores, setTodayChores] = useState<ChoreAssignment[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<CalendarEvent[]>([]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [fabOpen, setFabOpen] = useState(false);

  // Computed values
  const greeting = useMemo(() => getGreeting(), []);
  const displayName = userProfile?.display_name || userProfile?.full_name?.split(' ')[0] || 'there';
  const todayDate = useMemo(() => formatDate(new Date()), []);
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  // Load data - only when householdId is available
  useEffect(() => {
    // Don't fetch until we have householdId
    if (!householdId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);

      const now = new Date();
      const endOfWeek = new Date(now);
      endOfWeek.setDate(endOfWeek.getDate() + 7);

      const [choresRes, eventsRes, membersRes] = await Promise.all([
        // Today's task assignments with task and assignee data
        supabase
          .from('task_assignments')
          .select('*, chore:tasks(*), assignee:family_members!assigned_to(*)')
          .eq('household_id', householdId)
          .eq('due_date', today)
          .order('due_time', { nullsFirst: false }),
        // Upcoming events (next 3)
        supabase
          .from('calendar_events')
          .select('*, family_member:family_members(*)')
          .eq('household_id', householdId)
          .gte('start_time', now.toISOString())
          .lte('start_time', endOfWeek.toISOString())
          .order('start_time')
          .limit(3),
        // Family members
        supabase
          .from('family_members')
          .select('*')
          .eq('household_id', householdId)
          .order('sort_order'),
      ]);

      if (choresRes.data) setTodayChores(choresRes.data);
      if (eventsRes.data) setUpcomingEvents(eventsRes.data);
      if (membersRes.data) setFamilyMembers(membersRes.data);

      setLoading(false);
    };

    fetchData();

    // Set up realtime subscriptions
    const choresChannel = supabase
      .channel('home-tasks')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_assignments' }, () => {
        fetchData();
      })
      .subscribe();

    const eventsChannel = supabase
      .channel('home-events')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'calendar_events' }, () => {
        fetchData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(choresChannel);
      supabase.removeChannel(eventsChannel);
    };
  }, [householdId, today]);

  // Stats calculations
  const choreStats = useMemo(() => {
    const completed = todayChores.filter(c => c.is_completed).length;
    const total = todayChores.length;
    return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  }, [todayChores]);

  // Convert upcoming events to list items
  const eventItems: ListItemProps[] = upcomingEvents.map(event => {
    const eventDate = new Date(event.start_time);
    const isToday = eventDate.toDateString() === new Date().toDateString();
    const isTomorrow = eventDate.toDateString() === new Date(Date.now() + 86400000).toDateString();

    let dateLabel = eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    if (isToday) dateLabel = 'Today';
    else if (isTomorrow) dateLabel = 'Tomorrow';

    return {
      id: event.id,
      label: event.title,
      description: event.all_day ? `${dateLabel} - All day` : `${dateLabel} at ${formatEventTime(event.start_time)}`,
      start: {
        element: (
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: event.color || 'var(--kff-foundry-blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 18,
            }}
          >
            {event.icon || <IonIcon icon={calendarOutline} />}
          </div>
        ),
      },
      end: event.family_member ? {
        element: (
          <Badge variant="neutral" size="sm">
            {event.family_member.nickname || event.family_member.name.split(' ')[0]}
          </Badge>
        ),
      } : undefined,
    };
  });

  // Quick stat card component
  const StatCard: React.FC<{
    icon: string;
    iconColor: string;
    label: string;
    value: string | number;
    subvalue?: string;
    onClick?: () => void;
  }> = ({ icon, iconColor, label, value, subvalue, onClick }) => (
    <div
      className="stat-card"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="stat-card__icon" style={{ backgroundColor: iconColor }}>
        <IonIcon icon={icon} />
      </div>
      <div className="stat-card__content">
        <div className="stat-card__value">{value}</div>
        <div className="stat-card__label">{label}</div>
        {subvalue && <div className="stat-card__subvalue">{subvalue}</div>}
      </div>
    </div>
  );

  // Show loading state while auth or household is loading
  if (authLoading || !householdId) {
    return (
      <PageShell title="Home" headerType="page" showSettings noPadding>
        <Container size="lg" padding="md">
          <Stack gap="lg">
            {/* Hero Skeleton */}
            <Stack gap="xs">
              <IonSkeletonText animated style={{ width: '60%', height: 32 }} />
              <IonSkeletonText animated style={{ width: '40%', height: 20 }} />
            </Stack>
            {/* Stats Skeleton */}
            <AutoGrid minColumnWidth="200px" gap="md">
              <IonSkeletonText animated style={{ width: '100%', height: 90, borderRadius: 12 }} />
              <IonSkeletonText animated style={{ width: '100%', height: 90, borderRadius: 12 }} />
              <IonSkeletonText animated style={{ width: '100%', height: 90, borderRadius: 12 }} />
            </AutoGrid>
          </Stack>
        </Container>
      </PageShell>
    );
  }

  return (
    <PageShell title="Home" headerType="page" showSettings noPadding>
      <Container size="lg" padding="md">
        <Stack gap="lg">
          {/* Hero Section - Greeting & Date */}
          <Stack gap="xs" className="home-hero">
            <h1 className="home-hero__greeting">
              {greeting}, {displayName}
            </h1>
            <p className="home-hero__date">{todayDate}</p>
          </Stack>

          {/* Quick Stats */}
          {loading ? (
            <AutoGrid minColumnWidth="200px" gap="md">
              <IonSkeletonText animated style={{ width: '100%', height: 90, borderRadius: 12 }} />
              <IonSkeletonText animated style={{ width: '100%', height: 90, borderRadius: 12 }} />
              <IonSkeletonText animated style={{ width: '100%', height: 90, borderRadius: 12 }} />
            </AutoGrid>
          ) : (
            <AutoGrid minColumnWidth="200px" gap="md">
              <StatCard
                icon={checkmarkCircle}
                iconColor="var(--ion-color-success)"
                label="Today's Chores"
                value={`${choreStats.completed}/${choreStats.total}`}
                subvalue={choreStats.total > 0 ? `${choreStats.percentage}% complete` : 'No chores today'}
                onClick={() => history.push('/chores')}
              />
              <StatCard
                icon={calendarOutline}
                iconColor="var(--kff-foundry-blue)"
                label="Upcoming Events"
                value={upcomingEvents.length}
                subvalue={upcomingEvents.length > 0 ? 'This week' : 'No events'}
                onClick={() => history.push('/calendar')}
              />
              <StatCard
                icon={peopleOutline}
                iconColor="var(--kff-forge-gold)"
                label="Family Members"
                value={familyMembers.length}
                onClick={() => history.push('/settings')}
              />
            </AutoGrid>
          )}

          {/* Today's Chores Preview */}
          {todayChores.length > 0 && (
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ fontFamily: 'var(--kff-heading-font)', fontSize: 16, fontWeight: 600, margin: 0 }}>
                  Today's Chores
                </h3>
                <Button
                  fill="clear"
                  size="small"
                  onClick={() => history.push('/chores')}
                >
                  View All <IonIcon icon={chevronForwardOutline} style={{ marginLeft: 4 }} />
                </Button>
              </div>
              <Stack gap="sm" className="chores-preview">
                {todayChores.slice(0, 4).map(assignment => (
                  <div
                    key={assignment.id}
                    className={`chore-preview-item ${assignment.is_completed ? 'completed' : ''}`}
                  >
                    <span className="chore-preview-item__icon">
                      {assignment.chore?.icon || '📋'}
                    </span>
                    <div className="chore-preview-item__content">
                      <span className="chore-preview-item__name">
                        {assignment.chore?.name}
                      </span>
                      <span className="chore-preview-item__assignee">
                        {assignment.assignee?.nickname || assignment.assignee?.name?.split(' ')[0] || 'Unassigned'}
                      </span>
                    </div>
                    {assignment.is_completed && (
                      <IonIcon icon={checkmarkCircle} className="chore-preview-item__check" />
                    )}
                  </div>
                ))}
                {todayChores.length > 4 && (
                  <div className="chores-preview__more">
                    +{todayChores.length - 4} more chores
                  </div>
                )}
              </Stack>
            </Card>
          )}

          {/* Upcoming Events */}
          {upcomingEvents.length > 0 && (
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ fontFamily: 'var(--kff-heading-font)', fontSize: 16, fontWeight: 600, margin: 0 }}>
                  Upcoming Events
                </h3>
                <Button
                  fill="clear"
                  size="small"
                  onClick={() => history.push('/calendar')}
                >
                  Calendar <IonIcon icon={chevronForwardOutline} style={{ marginLeft: 4 }} />
                </Button>
              </div>
              <List items={eventItems} variant="default" size="sm" />
            </Card>
          )}

          {/* Quick Actions */}
          <Stack gap="md">
            <h3 className="home-actions__title">Quick Actions</h3>
            <Row gap="md">
              <Col mobile={4}>
                <Button
                  fill="outline"
                  onClick={() => history.push('/chores?action=add')}
                  style={{ flexDirection: 'column', height: 'auto', padding: '16px 12px', gap: 8, width: '100%' }}
                >
                  <IonIcon icon={addOutline} style={{ fontSize: 24 }} />
                  <span>Assign Chore</span>
                </Button>
              </Col>
              <Col mobile={4}>
                <Button
                  fill="outline"
                  onClick={() => history.push('/calendar?action=add')}
                  style={{ flexDirection: 'column', height: 'auto', padding: '16px 12px', gap: 8, width: '100%' }}
                >
                  <IonIcon icon={calendarOutline} style={{ fontSize: 24 }} />
                  <span>Add Event</span>
                </Button>
              </Col>
              <Col mobile={4}>
                <Button
                  fill="outline"
                  onClick={() => history.push('/calendar')}
                  style={{ flexDirection: 'column', height: 'auto', padding: '16px 12px', gap: 8, width: '100%' }}
                >
                  <IonIcon icon={timeOutline} style={{ fontSize: 24 }} />
                  <span>View Week</span>
                </Button>
              </Col>
            </Row>
          </Stack>

          {/* Empty state if nothing to show */}
          {!loading && todayChores.length === 0 && upcomingEvents.length === 0 && (
            <Card style={{ textAlign: 'center' }}>
              <Stack gap="md" align="center" style={{ padding: '24px 16px' }}>
                <div style={{ fontSize: 48 }}>🏠</div>
                <h3 style={{ fontFamily: 'var(--kff-heading-font)', margin: 0 }}>
                  All caught up!
                </h3>
                <p style={{ color: 'var(--ion-color-medium)', margin: 0 }}>
                  No chores or events scheduled for today.
                </p>
                <Button variant="primary" onClick={() => history.push('/chores?action=add')}>
                  Assign a Chore
                </Button>
              </Stack>
            </Card>
          )}
        </Stack>
      </Container>

      {/* Speed Dial FAB for Quick Add */}
      <IonFab
        vertical="bottom"
        horizontal="end"
        slot="fixed"
        className="home-speed-dial"
        activated={fabOpen}
      >
        <IonFabButton
          onClick={() => setFabOpen(!fabOpen)}
          className={fabOpen ? 'fab-open' : ''}
        >
          <IonIcon icon={fabOpen ? closeOutline : addOutline} />
        </IonFabButton>
        <IonFabList side="top">
          {/* Announcement */}
          <IonFabButton
            onClick={() => {
              setFabOpen(false);
              history.push('/settings/announcements?action=add');
            }}
            data-desc="Announcement"
          >
            <IonIcon icon={megaphoneOutline} />
          </IonFabButton>

          {/* Chore */}
          <IonFabButton
            onClick={() => {
              setFabOpen(false);
              history.push('/chores?action=add');
            }}
            data-desc="Assign Chore"
          >
            <IonIcon icon={listOutline} />
          </IonFabButton>

          {/* Event */}
          <IonFabButton
            onClick={() => {
              setFabOpen(false);
              history.push('/calendar?action=add');
            }}
            data-desc="Add Event"
          >
            <IonIcon icon={calendarOutline} />
          </IonFabButton>

          {/* Grocery Item */}
          <IonFabButton
            onClick={() => {
              setFabOpen(false);
              history.push('/groceries?action=add');
            }}
            data-desc="Grocery Item"
          >
            <IonIcon icon={cartOutline} />
          </IonFabButton>
        </IonFabList>
      </IonFab>

      {/* FAB backdrop */}
      {fabOpen && (
        <div
          className="fab-backdrop"
          onClick={() => setFabOpen(false)}
        />
      )}
    </PageShell>
  );
};

export default HomePage;
